import React, { useState, useEffect, useCallback } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { ApiClient, AuthTokens } from '../../lib/ApiClient';

interface PlaidProviderProps {
  apiClient: ApiClient;
  authTokens: AuthTokens | null;
  setAuthTokens: (tokens: AuthTokens | null) => void;
  simulationId: string | null;
  isConnecting: boolean;
  onSuccess: (enrollmentId: string) => void;
  onError: (error: string) => void;
  setIsLoading: (loading: boolean) => void;
}

const PlaidProvider: React.FC<PlaidProviderProps> = ({
  apiClient,
  authTokens,
  setAuthTokens,
  simulationId,
  isConnecting,
  onSuccess,
  onError,
  setIsLoading
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);

  // Clear linkToken when simulationId changes (e.g., user starts over)
  // This ensures we get a fresh Plaid session for the new simulation
  useEffect(() => {
    setLinkToken(null);
  }, [simulationId]);

  // Initialize Plaid Link Token
  useEffect(() => {
    const initializePlaid = async () => {
      try {
        // Require both auth tokens AND simulation ID to be set by parent
        // Do NOT create anonymous user here - parent is responsible for that
        // This prevents race conditions where a new user is created but old simulation ID is used
        const currentTokens = authTokens || apiClient.getAuthTokens();
        if (!currentTokens) {
          console.log('PlaidProvider: No auth tokens available, waiting for parent to initialize');
          return; // Parent should handle creating user and simulation together
        }

        if (!simulationId) {
          console.log('PlaidProvider: No simulation ID available, waiting for parent to initialize');
          return; // Don't initialize if we don't have a simulation ID yet
        }

        console.log('Creating Plaid link token...');
        const sessionData = await apiClient.createPlaidSession(simulationId);
        setLinkToken(sessionData.link_token);
        console.log('Plaid link token created');
      } catch (error) {
        console.error('Failed to initialize Plaid:', error);
        onError('Failed to initialize Plaid');
      }
    };

    if (simulationId && !linkToken) {
      initializePlaid();
    }
  }, [simulationId, authTokens, apiClient, onError, linkToken]);

  const onPlaidSuccess = useCallback(async (public_token: string, metadata: any) => {
    try {
      setIsLoading(true);
      setIsInitializing(true);

      if (!simulationId) {
        throw new Error('No simulation ID available');
      }

      console.log('Plaid success:', { public_token, metadata });

      // Exchange public token for access token and create enrollment
      const exchangeData = await apiClient.exchangePlaidToken(simulationId, {
        public_token,
        metadata
      });

      console.log('Plaid enrollment created:', exchangeData.enrollment_id);
      onSuccess(exchangeData.enrollment_id);
      setIsInitializing(false);
    } catch (err) {
      console.error('Error handling Plaid success:', err);
      onError(`Failed to process Plaid connection: ${err instanceof Error ? err.message : 'Unknown error'}`);
      setIsLoading(false);
      setIsInitializing(false);
    }
  }, [simulationId, apiClient, onSuccess, onError, setIsLoading]);

  const onPlaidExit = useCallback((err: any, metadata: any) => {
    console.log('Plaid exit:', { err, metadata });
    setIsInitializing(false);
    
    if (err) {
      console.error('Plaid error:', err);
      onError(`Plaid connection failed: ${err.error_message || 'Unknown error'}`);
    }
  }, [onError]);

  const onPlaidEvent = useCallback((eventName: string, metadata: any) => {
    console.log('Plaid event:', eventName, metadata);
  }, []);

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: onPlaidSuccess,
    onExit: onPlaidExit,
    onEvent: onPlaidEvent,
  });

  const handleConnect = async () => {
    if (!ready || !linkToken) {
      onError('Plaid not ready');
      return;
    }

    setIsInitializing(true);
    open();
  };

  // Button is disabled if: connecting, initializing, no auth, no simulation, no link token, or not ready
  const hasAuth = authTokens || apiClient.getAuthTokens();
  const isButtonDisabled = isConnecting || isInitializing || !hasAuth || !simulationId || !ready || !linkToken;

  return (
    <button
      onClick={handleConnect}
      disabled={isButtonDisabled}
      style={{
        background: isButtonDisabled ? '#ddd' : 'linear-gradient(135deg, #13a1d6, #0f7fa3)',
        color: 'white',
        padding: '15px 25px',
        border: 'none',
        borderRadius: '8px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: isButtonDisabled ? 'not-allowed' : 'pointer',
        width: '100%'
      }}
    >
      {isInitializing ? 'Connecting...' :
       isConnecting ? 'Processing...' :
       (!authTokens && !apiClient.getAuthTokens()) ? 'Initializing...' :
       !simulationId ? 'Initializing...' :
       !linkToken ? 'Loading Plaid...' :
       !ready ? 'Preparing...' :
       '🔒 Connect via Plaid'}
    </button>
  );
};

export default PlaidProvider;